import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FormProvider, useForm } from "react-hook-form";
import { supplyConfigurationApi } from "@/main/api";
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { SupplyConfigurationGetAllResponse, SupplyConfigurationResponse } from "@/main/models/response";
import { KeyValue } from "@/main/models/common";
import { SupplyConfigurationSaveRequest } from "@/main/models/request";
import { SupplierTypeEnum } from "@/main/services/enum/SupplierTypeEnum";
import { SpinnerLoading } from "../../styles";
import KOGSupplyConfigBoxGridMultiCheckbox from "../../supplyConfigBoxGridMultiCheckbox/supplyConfigBoxGridMultiCheckbox";
import useAsyncModalConfirmReason from "@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";
import * as S from "./styles";

interface Props {
	closeModal: () => void;
	guidSupplierParams?: string | null;
	setSupplyConfig: React.Dispatch<React.SetStateAction<SupplyConfigurationResponse[]>>;
	watcherCountry?: KeyValue | null;
	dataSupplierTypes: KeyValue[];
	sizeColumnTypes: string;
}
export const INITIAL_FORM_STATE = {
	guidSupplier: "",
	selectAllSupplierType: [] as {
		idSupplierType: number;
		isCheck: boolean;
	}[],
	vehicles: [
		{
			guidVehicleManufacturer: "",
			nameVehicleManufacturer: "",
			supplierTypes: [] as {
				idSupplierType: number;
				isCheck: boolean;
			}[],
		},
	],
};
export type ISupplyCongigFormSave = typeof INITIAL_FORM_STATE;

const mountSupplierTypeOption = (supplierType: number, hasKeysSupplierTypes: SupplierTypeEnum[]) => ({
	idSupplierType: supplierType,
	isCheck: hasKeysSupplierTypes.some((item) => item === supplierType),
});

function SupplyConfigRegister({
	closeModal,
	guidSupplierParams,
	setSupplyConfig,
	dataSupplierTypes,
	sizeColumnTypes,
}: Props) {
	const methods = useForm<ISupplyCongigFormSave>({
		defaultValues: INITIAL_FORM_STATE,
	});
	const { handleSubmit } = methods;
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
	const [isLoadingLoad, setIsLoadingLoad] = useState<boolean>(false);
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();

	function updateOptions(response: SupplyConfigurationGetAllResponse[]) {
		const updateVehicles = response.map((item) => ({
			guidVehicleManufacturer: item.guidVehicleManufacturer,
			nameVehicleManufacturer: item.nameVehicleManufacturer,
			supplierTypes: dataSupplierTypes
				.map((supplierType) => mountSupplierTypeOption(Number(supplierType.key), item.supplierTypes))
				.sort((a, b) => a.idSupplierType - b.idSupplierType),
		}));

		const INITIAL_ALL_SELECTED = dataSupplierTypes.map((item) => {
			const isChecked = updateVehicles.every((field) =>
				field.supplierTypes.some(
					(supplierType) => supplierType.idSupplierType === Number(item.key) && supplierType.isCheck,
				),
			);
			return {
				idSupplierType: Number(item.key),
				isCheck: isChecked,
			};
		});

		methods.reset({
			guidSupplier: String(guidSupplierParams),
			selectAllSupplierType: INITIAL_ALL_SELECTED,
			vehicles: updateVehicles,
		});
	}
	async function onLoadOptions() {
		if (!isNullUndefined(guidSupplierParams)) {
			setIsLoadingLoad(true);
			const requestForm = {
				guidSupplier: String(guidSupplierParams),
			};

			supplyConfigurationApi
				.getAll(requestForm)
				.then((response) => {
					updateOptions(response.data.result);
				})
				.finally(() => {
					setIsLoadingLoad(false);
				});
		}
	}
	async function onSubmitSave(data: ISupplyCongigFormSave) {
		const resultConfirm = await getConfirmationReason(
			getTranslateByKey("information.confirmChangeTitle"),
			ReasonTypeEnum.ChangeCompany,
		);

		if (!resultConfirm.confirmed) return;

		let filesRequest = undefined;
		if (resultConfirm.files && resultConfirm.files.length > 0) {
			filesRequest = await Promise.all(
				resultConfirm.files.map(async (item) => {
					const base64DataUrl = await toBase64(item);
					return {
						fileName: item.name,
						contentType: item.type,
						fileBase64: base64DataUrl as string,
					};
				}),
			);
		}

		try {
			setIsLoadingSave(true);
			const requestSave: SupplyConfigurationSaveRequest = {
				guidSupplier: guidSupplierParams,
				vehicleManufacturers: data.vehicles.map((item) => ({
					guidVehicleManufacturer: item.guidVehicleManufacturer,
					supplierTypes: item.supplierTypes
						.filter((supplierType) => supplierType.isCheck)
						.map((supplierType) => supplierType.idSupplierType),
				})),
				changeJustification: {
					guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
					reasonDescription: resultConfirm?.observation || null,
					filesUpload: filesRequest,
				},
			};
			await supplyConfigurationApi.saveSupplyConfiguration(requestSave);

			const requestForm = {
				guidSupplier: String(guidSupplierParams),
			};
			const [resultGet, resultGetAll] = await Promise.all([
				supplyConfigurationApi.getbysupplier(requestForm),
				supplyConfigurationApi.getAll(requestForm),
			]);

			setSupplyConfig(resultGet.data.result);
			updateOptions(resultGetAll.data.result);

			closeModal();
			toast.success(getTranslateByKey("supplyConfiguration.supplyConfigurationSavedSuccessfully"));
		} catch {
		} finally {
			setIsLoadingSave(false);
		}
	}

	useEffect(() => {
		onLoadOptions();
	}, [guidSupplierParams]);

	const rowHeaders = [getTranslateByKey("information.make")];
	if (isLoadingLoad) return <SpinnerLoading />;

	return (
		<S.ContainerForm onSubmit={handleSubmit(onSubmitSave)} $hasScroll noValidate>
			<FormProvider {...methods}>
				<KOGSupplyConfigBoxGridMultiCheckbox
					rowHeaders={rowHeaders}
					sizeColumnTypes={sizeColumnTypes}
					dataSupplierTypes={dataSupplierTypes}
				/>
			</FormProvider>
			<div className="d-flex justify-content-center">
				<SecondaryButton
					type="submit"
					style={{ marginTop: 10 }}
					isLoading={isLoadingSave}
					id="id-registerSupplier-button-saveSupplyConfiguration"
				>
					{getTranslateByKey("information.save")}
				</SecondaryButton>
			</div>
			<ModalConfirmationReason closeByOverlay={false} />
		</S.ContainerForm>
	);
}

export default SupplyConfigRegister;
